import { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import { JobFormRule, JobFormRuleFieldType } from "../../types/jobs";

/**
 * Job form rules: "a task with service A needs field B" - the form's half of
 * backend/service/job_form_rules.py, which has the final say when the job is
 * saved. Here they mark the fields required as soon as such a service is
 * picked, and stop a save the backend would refuse anyway.
 *
 * - To let a rule require another field, add it to JOB_FORM_RULE_FIELDS (and
 *   JobFormRuleFieldType) and to FORM_FIELDS in the backend.
 * - To trigger rules by something other than a service, extend `triggeredBy`
 *   and its backend twin.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The part of a job form task the rules read. */
export type RuleTaskValues = {
  equipment: string | number | null;
  issues: OptionRecord[];
  services: OptionRecord[];
  description: string;
  attachments: unknown[];
};

/** The part of the job form the rules read. */
export type RuleJobValues = {
  client_id: string | number | null;
  planned_for: string | null;
  assigned_to: OptionRecord[];
  external_service_selected: boolean;
  external_service: OptionRecord | null;
  description: string;
  tasks: RuleTaskValues[];
};

type FieldLabel = {
  /** The field's name in the job API, which is also where its error goes. */
  errorKey: string;
  labelKey: string;
};

/** A field of the task that triggered the rule, or of the job as a whole. */
type FormField =
  | (FieldLabel & { onTask: true; isFilled: (task: RuleTaskValues) => boolean })
  | (FieldLabel & { onTask: false; isFilled: (job: RuleJobValues) => boolean });

const hasValue = (value: string | number | null | undefined) =>
  value !== null && value !== undefined && value !== "";

const hasText = (value: string | null | undefined) => Boolean(value?.trim());

export const JOB_FORM_RULE_FIELDS: Record<JobFormRuleFieldType, FormField> = {
  E: { onTask: true, errorKey: "equipment", labelKey: "Equipment", isFilled: (task) => hasValue(task.equipment) },
  I: { onTask: true, errorKey: "issues", labelKey: "Issues", isFilled: (task) => task.issues.length > 0 },
  T: {
    onTask: true,
    errorKey: "description",
    labelKey: "Task description",
    isFilled: (task) => hasText(task.description),
  },
  A: {
    onTask: true,
    errorKey: "attachment_ids",
    labelKey: "Attachments",
    isFilled: (task) => task.attachments.length > 0,
  },
  C: { onTask: false, errorKey: "client_id", labelKey: "Client", isFilled: (job) => hasValue(job.client_id) },
  P: { onTask: false, errorKey: "planned_for", labelKey: "Planned for", isFilled: (job) => hasValue(job.planned_for) },
  // Handing the job over to an external service assigns it as well.
  W: {
    onTask: false,
    errorKey: "assigned_to",
    labelKey: "Assigned to",
    isFilled: (job) =>
      job.assigned_to.length > 0 || (job.external_service_selected && job.external_service !== null),
  },
  D: {
    onTask: false,
    errorKey: "description",
    labelKey: "Job description",
    isFilled: (job) => hasText(job.description),
  },
};

/** Every field type in the order the rule screens list them, task fields first. */
export const JOB_FORM_RULE_FIELD_TYPES = Object.keys(JOB_FORM_RULE_FIELDS) as JobFormRuleFieldType[];

/** Field type -> titles of the services that require it. */
export type RuleRequirements = Partial<Record<JobFormRuleFieldType, string[]>>;

export type JobFormRequirements = {
  job: RuleRequirements;
  /** One entry per task, in the form's order. */
  tasks: RuleRequirements[];
};

/** Titles of the task's services that trigger the rule; empty when it does not apply. */
const triggeredBy = (rule: JobFormRule, task: RuleTaskValues): string[] => {
  const taskServiceIds = new Set(task.services.map((service) => String(service.id)));
  return rule.services
    .filter((service) => taskServiceIds.has(String(service.id)))
    .map((service) => service.title);
};

/** Which fields the rules require for the form as it stands. */
export const getJobFormRequirements = (
  rules: JobFormRule[],
  values: RuleJobValues
): JobFormRequirements => {
  const job: RuleRequirements = {};
  const tasks: RuleRequirements[] = values.tasks.map(() => ({}));

  rules.forEach((rule) => {
    values.tasks.forEach((task, index) => {
      const serviceTitles = triggeredBy(rule, task);
      if (serviceTitles.length === 0) return;

      rule.required_fields.forEach(({ field_type }) => {
        const field = JOB_FORM_RULE_FIELDS[field_type];
        // A field type this version of the form does not know yet.
        if (!field) return;

        const requirements = field.onTask ? tasks[index] : job;
        const titles = requirements[field_type] ?? [];
        requirements[field_type] = [
          ...titles,
          ...serviceTitles.filter((title) => !titles.includes(title)),
        ];
      });
    });
  });

  return { job, tasks };
};

const requiredMessage = (serviceTitles: string[], t: Translate) =>
  t(
    serviceTitles.length === 1
      ? "Required for the service: {{services}}."
      : "Required for the services: {{services}}.",
    { services: serviceTitles.join(", ") }
  );

/**
 * Errors for the required fields left empty, shaped like the backend's
 * (task errors under `tasks`, one entry per task), or null when none is.
 */
export const findJobFormRuleErrors = (
  requirements: JobFormRequirements,
  values: RuleJobValues,
  t: Translate
): Record<string, unknown> | null => {
  const errors: Record<string, unknown> = {};

  (Object.entries(requirements.job) as [JobFormRuleFieldType, string[]][]).forEach(
    ([fieldType, titles]) => {
      const field = JOB_FORM_RULE_FIELDS[fieldType];
      if (!field.onTask && !field.isFilled(values)) {
        errors[field.errorKey] = [requiredMessage(titles, t)];
      }
    }
  );

  const taskErrors = values.tasks.map((task, index) => {
    const rowErrors: Record<string, string[]> = {};
    (Object.entries(requirements.tasks[index] ?? {}) as [JobFormRuleFieldType, string[]][]).forEach(
      ([fieldType, titles]) => {
        const field = JOB_FORM_RULE_FIELDS[fieldType];
        if (field.onTask && !field.isFilled(task)) {
          rowErrors[field.errorKey] = [requiredMessage(titles, t)];
        }
      }
    );
    return rowErrors;
  });
  if (taskErrors.some((rowErrors) => Object.keys(rowErrors).length > 0)) {
    errors.tasks = taskErrors;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};
